import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fields } from 'src/components/utils/fields'
import { useMediaQuery } from '@mui/material'
import { BufferService, LoadingContext, M_Button, M_Fieldset, M_Header, M_MessageBoard, NavigateContext } from 'src/M_Components'
import { OpenInFullOutlined, Refresh, AddCircleOutline, DeleteOutline, EditOutlined, ListAlt } from '@mui/icons-material'
import { DataTable } from 'primereact/datatable/datatable.cjs.js'
import { Column } from 'primereact/column/column.cjs.js'
import { Dialog } from 'primereact/dialog/dialog.cjs.js'


const dialogBtnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 18px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.875rem',
    transition: 'opacity 0.15s ease',
}

const cancelBtn = {
    ...dialogBtnBase,
    backgroundColor: 'var(--color-red, #e53935)',
    color: '#fff',
}

const saveBtn = {
    ...dialogBtnBase,
    backgroundColor: 'var(--color-green, #43a047)',
    color: '#fff',
}


export const MovementTypeCrud = () => {
    const [id, setId] = useState(sessionStorage.getItem('selectedTab'))
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const [data, setData] = useState({})
    const [open, setOpen] = useState(false)
    const [passedRow, setPassedRow] = useState({})
    const [operation, setOperation] = useState('')
    const [isFirstTime, setIsFirstTime] = useState(true)
    const [leavingPage, setLeavingPage] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingId, setEditingId] = useState(null)

    // ─── Equipment Status sub-grid state ──────────────────────────────────────
    const [statusLinks, setStatusLinks] = useState([])
    const [pendingLinks, setPendingLinks] = useState([])
    const [statusLinksLoading, setStatusLinksLoading] = useState(false)
    const [statusDialogOpen, setStatusDialogOpen] = useState(false)
    const [editingLink, setEditingLink] = useState(null)
    const editingLinkRef = useRef(null)         // ref to avoid stale closure in saveStatusLink
    const [statusDialogData, setStatusDialogData] = useState({})

    const movementTypeService = new BufferService('MovementType', 'secondary')
    const movementTypeEquipmentStatusService = new BufferService('MovementTypeEquipmentStatus', 'secondary')

    const sessionKeys = ['operation', 'data', 'open', 'passedRow', 'leavingPage']

    const matches = useMediaQuery('(min-width:1800px)')
    const matches2 = useMediaQuery('(min-width:1700px)')
    const matches3 = useMediaQuery('(min-width:1490px)')
    const matches4 = useMediaQuery('(min-width:1450px)')
    const matches5 = useMediaQuery('(min-width:1420px)')
    const matches6 = useMediaQuery('(min-width:1380px)')
    const matches7 = useMediaQuery('(min-width:1280px)')
    const matches8 = useMediaQuery('(min-width:1230px)')
    const matches9 = useMediaQuery('(min-width:1190px)')
    const matches10 = useMediaQuery('(min-width:1150px)')
    const matches11 = useMediaQuery('(min-width:970px)')
    const matches12 = useMediaQuery('(min-width:930px)')
    const matches13 = useMediaQuery('(min-width:850px)')
    const matches14 = useMediaQuery('(min-width:870px)')
    const matches15 = useMediaQuery('(min-width:615px)')
    const matches16 = useMediaQuery('(min-width:440px)')


    // ─── Field definitions ────────────────────────────────────────────────────

    const MovementTypeFields = [
        { display: t('Movement'), required: true, code: 'moveCode', type: 'text' },
        { display: t('Movement') + ' ' + t('Type') + ' ' + t('Name'), required: true, code: 'moveName', type: 'text' },
        { display: t('Empty'), type: 'checkBox', code: 'empty' },
        { display: t('Movement') + ' ' + t('Type') + ' ' + t('Comments'), code: 'moveTypeComments', type: 'textArea', fullWidth: true },
    ]

    const StatusDialogField = {
        display: t('Equipment') + ' ' + t('Status'),
        required: true,
        code: 'equipmentStatusCode',
        type: 'foreignKey',
        entity: 'EquipmentStatus',
        api: 'secondary',
        filterField: 'Code',
        labelFields: ['Code'],
        fields: [
            { key: 'Code', value: 'equipmentStatusCode' },
            { key: 'Id', value: 'equipmentStatusId' },
        ]
    }


    // ─── Lifecycle ────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isFirstTime) {
            ctx.setIsLoad(true)
            getSessionStorageData(id)
            setIsFirstTime(false)
            sessionStorage.setItem(`tab${id}`, JSON.stringify(sessionKeys))

            const editingMovementType = JSON.parse(sessionStorage.getItem('editingMovementType'))
            const operationType = sessionStorage.getItem('operationType')

            if (editingMovementType && (operationType === 'Edit' || operationType === 'Copy')) {
                editOrCopyMovementType(editingMovementType.Id, operationType === 'Copy')
                sessionStorage.removeItem('editingMovementType')
                sessionStorage.removeItem('operationType')
            } else if (operationType === 'Create') {
                setOperation('Create')
                sessionStorage.removeItem('operationType')
            }
        }
    }, [])

    useEffect(() => {
        const newId = sessionStorage.getItem('selectedTab')
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)
        }
    })

    useEffect(() => {
        sessionKeys.forEach((key) => {
            sessionStorageMachine(id, key, key)
        })
        // eslint-disable-next-line
    }, [id, operation, data, leavingPage])


    // ─── Session Storage ──────────────────────────────────────────────────────

    const sessionStorageMachine = (id, key, value) => {
        if (eval(value) instanceof Object && Object.keys(eval(value)).length === 0) return
        if (eval(value) instanceof String && eval(value) === '') return
        if (eval(value) instanceof Number && eval(value) === '') return
        // eslint-disable-next-line
        if (eval(value) instanceof Map) {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(Array.from(eval(value))))
        } else {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(eval(value)))
        }
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const tempO = JSON.parse(sessionStorage.getItem(`${sessionID}operation`))
        const tempD = JSON.parse(sessionStorage.getItem(`${sessionID}data`))
        const tempOS = JSON.parse(sessionStorage.getItem(`${sessionID}open`))
        const tempPR = JSON.parse(sessionStorage.getItem(`${sessionID}passedRow`))
        const tempLP = JSON.parse(sessionStorage.getItem(`${sessionID}leavingPage`))

        if ((tempO && tempO !== '') || (tempD && Object.keys(tempD).length !== 0) || tempOS || (tempPR && Object.keys(tempD).length !== 0)) {
            setOperation(new String(tempO))
            setData(new Object(tempD))
            setOpen(tempOS)
            setPassedRow(tempPR)
            setLeavingPage(tempLP || false)
            ctx.setIsLoad(false)
            return
        }
        get()
    }

    const get = () => {
        ctx.setIsLoad(false)
    }

    const refreshMovementType = () => {
        ctx.setIsLoad(true)
        sessionKeys.forEach(key => { sessionStorage.removeItem(`${id}${key}`) })
        setData({})
        setPassedRow({})
        setOpen(false)
        setOperation('')
        setLeavingPage(false)
        setIsEditMode(false)
        setEditingId(null)
        setStatusLinks([])
        setPendingLinks([])
        get()
        ctx.setIsLoad(false)
        ctx.toastMessages('success', t('Form refreshed'), '')
    }


    // ─── Duplicate check ──────────────────────────────────────────────────────

    const checkMovementTypeExists = async (code) => {
        if (!code) return false
        try {
            const response = await movementTypeService.filter(`?filter=Code eq '${code}'`)
            if (response.name === 'AxiosError' || !response.Items) return false
            return !!response.Items.find(item => item.Code === code)
        } catch (error) {
            console.error('Error checking movement type:', error)
            return false
        }
    }


    // ─── Status sub-grid — load ───────────────────────────────────────────────

    const loadStatusLinks = (movementTypeId) => {
        if (!movementTypeId) return
        setStatusLinksLoading(true)
        movementTypeEquipmentStatusService.filter(
            `/query?filter=MovementTypeId eq ${movementTypeId}&page=1&pagesize=100`
        ).then(response => {
            setStatusLinksLoading(false)
            if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('Failed to load') + ' ' + t('Equipment') + ' ' + t('Status'))
                return
            }
            setStatusLinks(response?.Items ?? [])
        }).catch(() => {
            setStatusLinksLoading(false)
            ctx.toastMessages('error', '', t('Failed to load') + ' ' + t('Equipment') + ' ' + t('Status'))
        })
    }


    // ─── Status sub-grid — dialog ─────────────────────────────────────────────

    const openStatusDialog = (link = null) => {
        console.log('openStatusDialog link:', link)
        editingLinkRef.current = link           // always set ref first — avoids stale closure
        setEditingLink(link)
        setStatusDialogData(link ? {
            equipmentStatusCode: link.EquipmentStatus?.Code || link.EquipmentStatusCode || '',
            equipmentStatusId: link.EquipmentStatusId,
        } : {})
        setStatusDialogOpen(true)
    }

    const closeStatusDialog = () => {
        editingLinkRef.current = null
        setEditingLink(null)
        setStatusDialogData({})
        setStatusDialogOpen(false)
    }

    const saveStatusLink = async () => {
        const currentEditingLink = editingLinkRef.current  // read from ref, not state
        console.log('currentEditingLink at save:', currentEditingLink)

        if (!statusDialogData.equipmentStatusId) {
            ctx.toastMessages('error', '', t('Missing required fields'))
            return
        }

        const newStatusId = parseInt(statusDialogData.equipmentStatusId)

        if (isEditMode) {
            // Duplicate check — same status already linked, excluding the one being edited
            const duplicate = statusLinks.find(link =>
                link.EquipmentStatusId === newStatusId &&
                (!currentEditingLink || link.Id !== currentEditingLink.Id)
            )
            if (duplicate) {
                ctx.toastMessages('error', '', t('This status is already linked to this Movement Type'))
                return
            }

            ctx.setIsLoad(true)
            try {
                if (currentEditingLink) {
                    // Update the existing link row — PUT instead of DELETE + POST
                    await movementTypeEquipmentStatusService.update(currentEditingLink.Id, {
                        MovementTypeId: parseInt(editingId),
                        EquipmentStatusId: newStatusId,
                    })
                    ctx.toastMessages('success', '', t('Status updated successfully'))
                } else {
                    // New link
                    await movementTypeEquipmentStatusService.post({
                        MovementTypeId: parseInt(editingId),
                        EquipmentStatusId: newStatusId,
                    })
                    ctx.toastMessages('success', '', t('Status added successfully'))
                }
            } catch (error) {
                ctx.toastMessages('error', '', t('Failed to save status link'))
            }
            ctx.setIsLoad(false)
            closeStatusDialog()
            loadStatusLinks(editingId)

        } else {
            // Create mode — hold in pendingLinks until MT is saved
            const duplicate = pendingLinks.find(l => parseInt(l.equipmentStatusId) === newStatusId)
            if (duplicate && (!currentEditingLink || parseInt(currentEditingLink.equipmentStatusId) !== newStatusId)) {
                ctx.toastMessages('error', '', t('This status is already added'))
                return
            }

            if (currentEditingLink) {
                // Replace the pending link being edited
                setPendingLinks(prev => prev.map(l =>
                    parseInt(l.equipmentStatusId) === parseInt(currentEditingLink.equipmentStatusId)
                        ? { equipmentStatusCode: statusDialogData.equipmentStatusCode, equipmentStatusId: newStatusId }
                        : l
                ))
            } else {
                setPendingLinks(prev => [...prev, {
                    equipmentStatusCode: statusDialogData.equipmentStatusCode,
                    equipmentStatusId: newStatusId,
                }])
            }
            closeStatusDialog()
        }
    }


    // ─── Status sub-grid — delete ─────────────────────────────────────────────

    const deleteStatusLink = async (link) => {
        if (isEditMode) {
            ctx.setIsLoad(true)
            try {
                await movementTypeEquipmentStatusService.delete(link.Id)
                ctx.toastMessages('success', '', t('Status removed successfully'))
                loadStatusLinks(editingId)
            } catch (error) {
                ctx.toastMessages('error', '', t('Failed to remove status link'))
            }
            ctx.setIsLoad(false)
        } else {
            setPendingLinks(prev => prev.filter(l => parseInt(l.equipmentStatusId) !== parseInt(link.equipmentStatusId)))
        }
    }


    // ─── Save MT ──────────────────────────────────────────────────────────────

    const onSave = async () => {
        if (!data.moveCode || !data.moveName) {
            ctx.toastMessages('error', '', t('Missing required fields'))
            return
        }

        // At least one status must be linked
        const currentLinks = isEditMode ? statusLinks : pendingLinks
        if (currentLinks.length === 0) {
            ctx.toastMessages('error', '', t('At least one Equipment Status must be linked'))
            return
        }

        if (!isEditMode || (isEditMode && data.moveCode !== data._originalCode)) {
            ctx.setIsLoad(true)
            const exists = await checkMovementTypeExists(data.moveCode)
            ctx.setIsLoad(false)
            if (exists) {
                ctx.toastMessages('error', '', `${t('Movement')} ${t('Type')} ${data.moveCode} ${t('alreadyExists')}`)
                return
            }
        }

        const movementTypeData = {
            Code: data.moveCode,
            Name: data.moveName,
            IsEmpty: data.empty || false,
            Comments: data.moveTypeComments || '',
        }

        ctx.setIsLoad(true)

        const savePromise = isEditMode && editingId
            ? movementTypeService.update(editingId, movementTypeData)
            : movementTypeService.post(movementTypeData)

        savePromise.then(async response => {
            if (response.name === 'AxiosError') {
                ctx.setIsLoad(false)
                handleError(response)
                return
            }

            // Create mode: POST all pending links now that we have the MT Id
            if (!isEditMode && pendingLinks.length > 0) {
                const savedId = response.Id
                try {
                    await Promise.all(pendingLinks.map(link =>
                        movementTypeEquipmentStatusService.post({
                            MovementTypeId: parseInt(savedId),
                            EquipmentStatusId: parseInt(link.equipmentStatusId),
                        })
                    ))
                } catch (linkError) {
                    console.error('Error saving status links:', linkError)
                    ctx.toastMessages('warning', '', t('Movement Type saved but some status links could not be saved'))
                }
            }

            ctx.setIsLoad(false)
            const successMessage = isEditMode
                ? t('MovementType') + t('updatedSucessfully')
                : t('MovementType') + t('savedSucessfully')
            ctx.toastMessages('success', '', successMessage)
            ctx2.closeTab(JSON.parse(id))

        }).catch(error => {
            ctx.setIsLoad(false)
            handleError(error)
        })
    }


    // ─── Edit / Copy ──────────────────────────────────────────────────────────

    const editOrCopyMovementType = async (movementTypeId, isCopy = false) => {
        ctx.setIsLoad(true)

        try {
            const response = await movementTypeService.getId(movementTypeId)
            ctx.setIsLoad(false)

            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('Failed to load') + ' ' + t('Movement') + ' ' + t('Type'))
                return
            }

            const movementTypeData = {
                moveCode: isCopy ? '' : (response.Code?.toString() || ''),
                moveName: response.Name,
                empty: response.IsEmpty,
                moveTypeComments: response.Comments,
                _originalCode: response.Code?.toString() || '',
                _isCopy: isCopy,
                _originalId: isCopy ? movementTypeId : null,
            }

            if (isCopy) {
                delete movementTypeData.Id
                delete movementTypeData.ExternalId
                delete movementTypeData.CreatedAt
                delete movementTypeData.UpdatedAt
                setIsEditMode(false)
                setEditingId(null)
                setOperation('Copy')
                setStatusLinks([])
                setPendingLinks([])
            } else {
                setIsEditMode(true)
                setEditingId(movementTypeId)
                setOperation('Edit')
                loadStatusLinks(movementTypeId)
            }

            setData(movementTypeData)

        } catch (error) {
            ctx.setIsLoad(false)
            console.error('Error loading movement type:', error)
            ctx.toastMessages('error', '', t('Failed to load') + ' ' + t('Movement') + ' ' + t('Type'))
        }
    }


    // ─── Error Handler ────────────────────────────────────────────────────────

    const handleError = (error) => {
        let errorMessage = t('Failed to create') + ' ' + t('Movement') + ' ' + t('Type')
        if (Array.isArray(data)) {
            errorMessage = data.map(e => `${e.field}: ${e.message}`).join('\n')
        } else if (data?.errors) {
            errorMessage = Object.entries(data.errors)
                .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                .join('; ')
        } else if (data?.message) {
            errorMessage = data.field ? `${data.field}: ${data.message}` : data.message
        } else if (data?.detail) {
            errorMessage = data.detail
        } else if (data?.title) {
            errorMessage = data.title
        } else if (error.message) {
            errorMessage = error.message
        }
        ctx.toastMessages('error', '', errorMessage)
    }

    // Normalised rows for the DataTable
    const gridRows = isEditMode
        ? statusLinks.map(link => ({
            id: link.Id,
            equipmentStatusId: link.EquipmentStatusId,
            equipmentStatusCode: link.EquipmentStatus?.Code || link.EquipmentStatusCode || '',
            _raw: link,
        }))
        : pendingLinks.map(link => ({
            id: null,
            equipmentStatusId: link.equipmentStatusId,
            equipmentStatusCode: link.equipmentStatusCode,
            _raw: link,
        }))


    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div>
            <M_Header
                element={
                    <>
                        <M_Button
                            tooltip={t("Back")}
                            label={t("Back")}
                            icon={<i className="pi pi-arrow-left iconTable" style={{ width: '20px', color: ctx.secondaryColor ? ctx.secondaryColor : 'var(--color-black)' }}></i>}
                            onClick={() => { setLeavingPage(true) }}
                        />
                        <M_Button
                            tooltip={t("Refresh")}
                            icon={<Refresh />}
                            label={t("Refresh")}
                            onClick={() => refreshMovementType()}
                        />
                        <M_Button
                            tooltip={t("Save")}
                            icon={<i className="pi pi-save "></i>}
                            label={t("Save")}
                            onClick={() => onSave()}
                        />
                    </>
                }
            />
            <div style={{ margin: '20px' }}>

                {/* ── Movement Type Details ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'auto' }}>
                    <M_Fieldset
                        labelInside
                        label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}><OpenInFullOutlined style={{ marginRight: '10px', scale: '1.3' }} /> {t('Movement') + ' ' + t('Type') + ' ' + t('Details')}</div>}
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: !matches16 && 'auto' || !matches9 && 'auto auto' || 'auto auto auto', justifyContent: 'space-evenly' }}>
                                {MovementTypeFields.map((field, index) => {
                                    if (field.type === 'separator') {
                                        return <div key={`separator-${index}`} style={{ gridColumn: '1 / -1', height: '0' }} />
                                    }
                                    if (field.fullWidth) {
                                        return (
                                            <div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>
                                                {fields(field, index, data, setData, false, null, '100%')}
                                            </div>
                                        )
                                    }
                                    return fields(field, index, data, setData, null, null, '100%')
                                })}
                            </div>
                        }
                    />
                </div>

                {/* ── Equipment Status sub-grid ── */}
                <div style={{ margin: '20px 0' }}>
                    <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '1rem' }}>
                                <ListAlt style={{ marginRight: '8px' }} />
                                {t('Equipment') + ' ' + t('Status')}
                            </span>
                            <M_Button
                                tooltip={t('Add') + ' ' + t('Status')}
                                label={t('Add') + ' ' + t('Status')}
                                icon={<AddCircleOutline />}
                                onClick={() => openStatusDialog()}
                            />
                        </div>
                        {statusLinksLoading ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>{t('Loading')}...</div>
                        ) : (
                            <DataTable
                                value={gridRows}
                                size="small"
                                stripedRows
                                emptyMessage={t('No statuses linked — add at least one before saving')}
                                style={{ fontSize: '0.875rem' }}
                            >
                                <Column field="equipmentStatusCode" header={t('Equipment') + ' ' + t('Status')} />
                                <Column
                                    header={t('Actions')}
                                    style={{ width: '90px' }}
                                    body={(row) => (
                                        <span style={{ display: 'flex', gap: '8px' }}>
                                            <EditOutlined
                                                style={{ cursor: 'pointer', color: '#495057' }}
                                                onClick={() => openStatusDialog(row._raw)}
                                            />
                                            <DeleteOutline
                                                style={{ cursor: 'pointer', color: '#dc3545' }}
                                                onClick={() => deleteStatusLink(row._raw)}
                                            />
                                        </span>
                                    )}
                                />
                            </DataTable>
                        )}
                    </div>
                </div>

                <div style={{ margin: '25px' }}></div>
            </div>

            {/* ── Status Dialog ── */}
            <Dialog
                visible={statusDialogOpen}
                onHide={closeStatusDialog}
                header={editingLink ? t('Edit') + ' ' + t('Status') : t('Add') + ' ' + t('Status')}
                style={{ width: '350px' }}
                footer={
                    <span style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '4px 0' }}>
                        <button
                            style={cancelBtn}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            onClick={closeStatusDialog}
                        >
                            <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                            {t('ButtonCancel')}
                        </button>
                        <button
                            style={saveBtn}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            onClick={saveStatusLink}
                        >
                            <i className="pi pi-save" style={{ fontSize: '0.8rem' }} />
                            {t('Save')}
                        </button>
                    </span>
                }
            >
                <div style={{ padding: '8px 0' }}>
                    {fields(StatusDialogField, 0, statusDialogData, setStatusDialogData, null, null, '100%')}
                </div>
            </Dialog>

            {/* ── Leave page confirmation ── */}
            <M_MessageBoard
                warning
                open={leavingPage}
                title={t("leavingPageTitle")}
                body={t("leavingPageBody")}
                footer={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-red)'} onClick={() => setLeavingPage(false)} label={t("ButtonCancel")} />
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-green)'} onClick={() => ctx2.closeTab(JSON.parse(id))} label={t("ButtonConfirm")} />
                    </span>
                }
            />
        </div>
    )
}